import { useField } from "@unform/core";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";

import Affix from "../../Components/Affix";
import HelpText from "../../Components/HelpText";
import * as S from "./styles";

// eslint-disable-next-line react/display-name
export const Core = forwardRef(({ gridArea, margin, ...props }, ref) => {
  const [value, setValue] = useState(props?.defaultValue);
  const inputRef = useRef(null);
  const { fieldName, defaultValue, registerField } = useField(props?.name);
  const [state, setState] = useState();

  const methods = useFormContext();
  const { setFocus, control } = methods ? methods : {};

  const { errors } = methods
    ? useFormState({
        control,
      })
    : {};

  useEffect(() => {
    if (errors && errors[fieldName]) {
      setFocus(fieldName);
    }
  }, [errors]);

  useEffect(() => {
    if (props?.mask) {
      registerField({
        name: fieldName,
        ref: ref ? ref.current : inputRef.current,
        path: "value",
        setValue(ref, value) {
          setValue(value);
        },
        clearValue() {
          setValue("");
        },
        getValue() {
          return value;
        },
      });
    } else {
      registerField({
        name: fieldName,
        ref: ref ? ref.current : inputRef.current,
        path: "value",
      });
    }
  }, [fieldName, registerField, value, props.mask, ref]);

  useEffect(() => {
    if (props?.value) {
      setValue(props.value);
    }
  }, [props?.value]);

  // Inputs mascarados são controlados por estado local. Quando o valor inicial
  // vem do react-hook-form (defaultValues — ex.: edição do projeto), populamos
  // o estado a partir do form para o campo não ficar em branco.
  useEffect(() => {
    if (!props?.mask || !methods?.getValues) return;
    if (value !== undefined && value !== null && value !== "") return;

    const formValue = methods.getValues(fieldName);

    if (formValue !== undefined && formValue !== null && formValue !== "")
      setValue(formValue);
    // eslint-disable-next-line
  }, []);

  const getInput = (mask) => {
    if (mask)
      return (
        <S.MaskedInputStyleStructure
          id={fieldName}
          ref={ref ? ref : inputRef}
          defaultValue={defaultValue}
          required={props?.required}
          size={props?.size}
          mask={mask}
          {...props}
          // react-input-mask quebra com null/undefined (chama value.split);
          // garantimos sempre uma string. Mantemos value/onChange após o
          // spread para não serem sobrescritos por props.value nulo.
          value={value ?? props?.value ?? ""}
          onChange={(e) => {
            setValue(e.target.value);
            props?.onChange && props?.onChange(e);
          }}
        />
      );

    return (
      <S.InputStylesStructure
        id={fieldName}
        ref={ref ? ref : inputRef}
        defaultValue={defaultValue}
        required={props?.required}
        size={props?.size}
        disabled={props?.disabled}
        {...props}
      />
    );
  };

  return (
    <S.Wrapper {...props} gridArea={gridArea} margin={margin}>
      {props?.label && (
        <S.Label htmlFor={fieldName} size={props?.size}>
          {props?.label} {props?.required && <S.RequiredDot />}
        </S.Label>
      )}
      <S.InputWrapper state={state} {...props}>
        {props?.prefix && (
          <Affix
            prefix="true"
            {...props?.prefix}
            size={props?.size}
            disabled={props.disabled}
          />
        )}
        {getInput(props?.mask)}
        {props?.suffix && (
          <Affix
            suffix="true"
            {...props?.suffix}
            size={props?.size}
            disabled={props.disabled}
          />
        )}
      </S.InputWrapper>
      {errors && errors[fieldName] ? (
        <HelpText
          id={fieldName}
          text={errors[fieldName]?.message}
          size={props?.size}
          setState={setState}
        />
      ) : null}
    </S.Wrapper>
  );
});
